import React, {
  useState,
  useEffect,
  useCallback,
} from 'react';
import { connect }             from 'react-redux';
import { Dispatch }            from 'redux';
import { ActionTypes }         from 'actions/types';
import { reducerType }         from 'reducer';
import Titles                  from './Titles';
// import TextInput only where still needed
import TextInput               from './TextInput';
// removed: MessageForm (no email/file sending UI)
// import UserData and Buttons unchanged
import UserData                from './UserData';
import Buttons                 from './buttons';
// removed: YouTube Iframe
import Loader                  from 'components/Loader';
import WhatsAppLink            from 'components/WhatsappLink';
import useSetFormInput         from './useSetFormInput';
import { ClickAwayListener }   from '@mui/base/ClickAwayListener';

import {
  modalStateType,
  teacherUrlStateType,
  userInfoType
} from 'types';
import { colors }              from 'utils/colors';
import { REGISTER_EMAIL_SAGA } from 'actions';
import {
  manualNavStateType,
  sizeStateType
} from 'reducer/types';
import { SET_MANUAL_NAV_STATE } from 'actions/manualNavigationActions';

import useModalStyle            from './useModalStyle';
import { makeStyles }           from 'makeStyles';

const useStyles = makeStyles()(() => ({
  root : {
    position:        'fixed',
    left:            0,
    height:          '100vh',
    width:           '100vw',
    display:         'flex',
    alignItems:      'center',
    justifyContent:  'center',
    backgroundColor: 'rgba(0,0,0,0.3)',
    transition:      'opacity 0.5s',
  },
  modal: {
    display:         'flex',
    flexFlow:        'column',
    backgroundColor: '#FFF',
    borderRadius:    '0.2em',
    boxShadow:       '3px 3px 3px rgba(0,0,0,0.05)'
  },
  buttonContainer: {
    borderTop:       '1px solid #DDD',
    minHeight:       70,
    width:           '100%',
    display:         'flex',
    alignItems:      'center',
    justifyContent:  'space-evenly',
  },
  button: {
    width:           330,
    height:          40,
    fontSize:        20,
    display:         'flex',
    alignItems:      'center',
    justifyContent:  'center',
    textAlign:       'center',
    backgroundColor: colors.primary,
    color:           '#FFF',
    fontWeight:      'bold',
    cursor:          'pointer',
    borderRadius:    '0.2em',
    boxShadow:       '2px 4px 9px rgba(0,0,0,0.1)',
    '&:hover': {
      opacity: 0.5
    }
  },
  loaderContainer: {
    height:          '100%',
    width:           '100%',
    border:          '1px solid transparent',
    borderRadius:    '0.25em',
    backgroundColor: '#FFF',
    display:         'flex',
    alignItems:      'center',
    justifyContent:  'center'
  },
  subExp: {
    textAlign:    'center',
    display:      'flex',
    flexFlow:     'column',
    width:        '100%',
    alignItems:   'center',
    justifyContent:'center',
    padding:      16,
    gap:          12
  }
}))

type ModalType = {
  sizeState:           sizeStateType;
  lang:                string;
  userInfo:            userInfoType;
  modalState:          modalStateType;
  teacherUrlState:     teacherUrlStateType;
  manualNavState:      manualNavStateType;
  registerEmail:       (payload: { userName: string, userEmail: string }) => void;
  setManualNavState:   (payload: manualNavStateType) => void;
}

export const Modal: React.FC<ModalType> = ({
  sizeState, // still injected though not needed for forms now
  lang,
  userInfo,
  modalState,
  teacherUrlState,
  manualNavState,
  registerEmail,
  setManualNavState
}) => {

  const { classes } = useStyles();

  const [modalIsDisplayed, setModalIsDisplayed] = useState(false);
  // keep these states in case Buttons relies on them; they won't be shown
  const [email, setEmail]               = useState('');
  const [name, setName]                 = useState('');
  const [message, setMessage]           = useState('');
  const [error, setError]               = useState('');
  const [lessonNumber, setLessonNumber] = useState('');

  // still wires defaults (harmless even if inputs removed)
  useSetFormInput(setMessage, setName, setEmail);

  useEffect(() => {
    const t = setTimeout(() => {
      setModalIsDisplayed(modalState.showModal);
    }, 150);
    return () => clearTimeout(t);
  }, [modalState.showModal]);

  const closeModal = useCallback(() => {
    import('./functions/closeModalFunc').then(func => {
      func.closeModalFunc(setName, setEmail, setMessage, setError);
    });
  }, []);

  // keep submitEmail flow for manualNavState.regEmail if other parts still use it
  const submitEmail = useCallback(() => {
    import('functions/actions/handleMessages').then(func => {
      func.submitEmailCB(userInfo.userEmail, email, lang, registerEmail, setError, closeModal);
    });
  }, [email, lang, userInfo.userEmail, registerEmail, closeModal]);

  useEffect(() => {
    if (manualNavState.regEmail) {
      submitEmail();
      setManualNavState({
        ...manualNavState,
        regEmail: false
      });
    }
  }, [manualNavState, setManualNavState, submitEmail]);

  const handleClickAway = () => {
    setTimeout(() => {
      // no forms anymore, but keep behavior: clicking away closes contact-related modals
      if (['contact us', 'getPlus', 'getGrade'].includes(modalState.modalVariant) && modalIsDisplayed) {
        closeModal();
      }
    }, 100);
  };

  const {
    rootStyle,
    modalStyle
  } = useModalStyle();

  return (
    <div
      dir="rtl"
      className={classes.root}
      style={rootStyle}
      data-testid="modalComp"
    >
      <ClickAwayListener onClickAway={handleClickAway}>
        <div className={classes.modal} style={modalStyle}>
          <Titles/>

          {/* Contact-style modals: show ONLY WhatsApp button now */}
          {
            ['contact us', 'getPlus', 'getGrade'].includes(modalState.modalVariant) &&
            <div className={classes.subExp}>
              <WhatsAppLink variant="" />
            </div>
          }

          {/* Subscription expired: ONLY WhatsApp button (no video, no form) */}
          {
            modalState.modalVariant === 'subscriptionExpired' &&
            <div className={classes.subExp}>
              <WhatsAppLink variant="" />
            </div>
          }

          {/* Join by lesson number (unchanged) */}
          {
            ['joinLesson', 'followLinkNotFound'].includes(modalState.modalVariant) &&
            <TextInput
              error={error}
              lessonNumber={lessonNumber}
              setLessonNumber={setLessonNumber}
              setError={setError}
            />
          }

          {/* Data modal (unchanged) */}
          { modalState.modalVariant === 'data' && <UserData/> }

          {/* Loader (unchanged) */}
          {
            modalState.modalVariant === 'loader' &&
            <div className={classes.loaderContainer}>
              <Loader/>
            </div>
          }

          {/* Buttons (unchanged). It can keep receiving name/email/message safely. */}
          {
            modalState.modalVariant !== 'loader' &&
          <Buttons
  teacherUrlState={teacherUrlState}
  email={email}
  name={name}
  message={message}
  lessonNumber={lessonNumber}
  setError={setError}
  closeModal={closeModal}
/>
          }
        </div>
      </ClickAwayListener>
    </div>
  );
};

const mapStateToProps = (reducer: reducerType) => ({
  modalState:     reducer.modal,
  lang:           reducer.components.lang,
  userInfo:       reducer.data.userInfo,
  manualNavState: reducer.manualNav,
  sizeState:      reducer.size
});

const mapDispatchToProps = (dispatch: Dispatch<ActionTypes>) => ({
  registerEmail:     (payload: { userName: string, userEmail: string }) => dispatch(REGISTER_EMAIL_SAGA(payload)),
  setManualNavState: (payload: manualNavStateType) => dispatch(SET_MANUAL_NAV_STATE(payload)),
});

export default connect(mapStateToProps, mapDispatchToProps)(Modal);
